import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowLeft, ArrowRight, Eye, EyeOff, Lock, Mail, ShieldCheck, Headphones, Info, MessageCircle } from 'lucide-react';
import Seo from '../components/Seo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { LOGIN } from '../constants/testIds/auth';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const WHATSAPP_URL = 'https://wa.me/5511974512072?text=Ol%C3%A1!%20Preciso%20de%20ajuda%20para%20acessar%20a%20CoreDesk.';

// Acesso a plataforma ainda nao esta integrado: o formulario valida os campos
// e, ao enviar, sempre exibe "E-mail ou senha incorretos". Nada e enviado.
const Login = () => {
  const [form, setForm] = useState({ email: '', senha: '', lembrar: true });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [notice, setNotice] = useState(null);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (!form.email) next.email = 'Informe seu e-mail.';
    else if (!EMAIL_RE.test(form.email)) next.email = 'Digite um e-mail válido.';
    if (!form.senha) next.senha = 'Informe sua senha.';
    setErrors(next);
    if (Object.keys(next).length) {
      setNotice(null);
      return;
    }
    setNotice('login');
    setForm((f) => ({ ...f, senha: '' }));
  };

  const fieldClass = (name) =>
    `h-12 pl-11 rounded-xl bg-white text-[15px] ${
      errors[name] ? 'border-red-400 focus-visible:ring-red-300' : 'border-slate-200 focus-visible:ring-[#FF5B24]/40'
    }`;

  return (
    <>
      <Seo
        title="Entrar"
        description="Acesse sua conta CoreDesk para gerenciar atendimentos, vendas e agentes de IA."
        path="/entrar"
      />
      <div className="min-h-screen grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] bg-white">
        {/* Formulario */}
        <div className="flex flex-col px-6 sm:px-10 lg:px-16 py-8">
          <div className="flex items-center justify-between">
            <Link to="/" aria-label="CoreDesk — página inicial">
              <img src="/logo.png" alt="CoreDesk" width="148" height="36" className="h-9 w-auto" />
            </Link>
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#0a0e27] transition-colors">
              <ArrowLeft size={15} /> Voltar ao site
            </Link>
          </div>

          <div className="flex-1 flex items-center justify-center py-12">
            <div className="w-full max-w-[400px]">
              <div className="text-xs font-bold uppercase tracking-widest text-[#FF5B24]">Área do cliente</div>
              <h1 className="mt-3 text-3xl md:text-[34px] leading-tight font-extrabold text-[#0a0e27]">Acesse sua conta</h1>
              <p className="mt-2 text-slate-500">Entre com o e-mail e a senha cadastrados pela sua empresa.</p>

              <form onSubmit={submit} noValidate className="mt-8 space-y-5">
                <div>
                  <label htmlFor="login-email" className="text-xs font-semibold text-slate-600">E-mail corporativo</label>
                  <div className="relative mt-1.5">
                    <Mail size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <Input
                      id="login-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      autoFocus
                      value={form.email}
                      onChange={onChange}
                      placeholder="voce@empresa.com.br"
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? 'login-email-error' : undefined}
                      className={fieldClass('email')}
                      data-testid={LOGIN.emailInput}
                    />
                  </div>
                  {errors.email && <p id="login-email-error" className="mt-1.5 text-xs font-medium text-red-500">{errors.email}</p>}
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <label htmlFor="login-senha" className="text-xs font-semibold text-slate-600">Senha</label>
                    <button
                      type="button"
                      onClick={() => setNotice('senha')}
                      className="text-xs font-semibold text-[#FF5B24] hover:text-[#e44c18]"
                      data-testid={LOGIN.forgotPasswordLink}
                    >
                      Esqueci minha senha
                    </button>
                  </div>
                  <div className="relative mt-1.5">
                    <Lock size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <Input
                      id="login-senha"
                      name="senha"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={form.senha}
                      onChange={onChange}
                      placeholder="Sua senha"
                      aria-invalid={!!errors.senha}
                      aria-describedby={errors.senha ? 'login-senha-error' : undefined}
                      className={`${fieldClass('senha')} pr-12`}
                      data-testid={LOGIN.passwordInput}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    >
                      {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                  {errors.senha && <p id="login-senha-error" className="mt-1.5 text-xs font-medium text-red-500">{errors.senha}</p>}
                </div>

                <label className="flex items-center gap-2.5 text-sm text-slate-600 cursor-pointer select-none">
                  <Checkbox
                    checked={form.lembrar}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, lembrar: v === true }))}
                    className="h-[18px] w-[18px] rounded-[5px] border-slate-300 data-[state=checked]:bg-[#FF5B24] data-[state=checked]:border-[#FF5B24]"
                  />
                  Manter conectado neste dispositivo
                </label>

                {notice === 'login' && (
                  <div role="alert" className="flex gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
                    <AlertCircle size={18} className="mt-0.5 shrink-0 text-red-500" />
                    <div className="text-sm text-slate-700">
                      <p className="font-semibold text-red-700">E-mail ou senha incorretos</p>
                      <p className="mt-1 leading-relaxed">Verifique os dados e tente novamente.</p>
                    </div>
                  </div>
                )}

                {notice === 'senha' && (
                  <div role="status" className="flex gap-3 rounded-xl border border-[#FF5B24]/20 bg-[#FFF4EF] p-4">
                    <Info size={18} className="mt-0.5 shrink-0 text-[#FF5B24]" />
                    <div className="text-sm text-slate-700">
                      <p className="font-semibold text-[#0a0e27]">Redefinição de senha pelo suporte</p>
                      <p className="mt-1 leading-relaxed">
                        Para redefinir sua senha, fale com nosso time de suporte. Atendemos de segunda a sexta, das 9h às 18h.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2">
                        <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-semibold text-[#0a0e27] hover:text-[#FF5B24]">
                          <MessageCircle size={15} /> Falar no WhatsApp
                        </a>
                        <Link to="/contato" className="inline-flex items-center gap-1.5 font-semibold text-[#0a0e27] hover:text-[#FF5B24]">
                          <Mail size={15} /> Outros contatos
                        </Link>
                      </div>
                    </div>
                  </div>
                )}

                <Button type="submit" className="btn-coral w-full h-12 rounded-xl text-[15px] font-semibold gap-2" data-testid={LOGIN.submitButton}>
                  Entrar <ArrowRight size={16} />
                </Button>
              </form>

              <div className="mt-8 pt-6 border-t border-slate-100 text-sm text-slate-500">
                Ainda não é cliente?{' '}
                <Link to="/contato" className="font-semibold text-[#0a0e27] hover:text-[#FF5B24]" data-testid={LOGIN.registerLink}>
                  Fale com um especialista
                </Link>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400">
            <span>© {new Date().getFullYear()} CoreDesk. Todos os direitos reservados.</span>
            <div className="flex gap-4">
              <Link to="/termos" className="hover:text-slate-600">Termos de uso</Link>
              <Link to="/privacidade" className="hover:text-slate-600">Privacidade</Link>
            </div>
          </div>
        </div>

        {/* Painel da marca */}
        <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-[#0a0e27] p-14 xl:p-16 text-white">
          <div className="pointer-events-none absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#FF5B24]/25 blur-[120px]" />
          <div className="pointer-events-none absolute -bottom-48 -left-32 h-[440px] w-[440px] rounded-full bg-[#FF8445]/10 blur-[120px]" />
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage: 'linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)',
              backgroundSize: '48px 48px',
              maskImage: 'radial-gradient(ellipse at 60% 40%, #000 30%, transparent 75%)',
              WebkitMaskImage: 'radial-gradient(ellipse at 60% 40%, #000 30%, transparent 75%)',
            }}
          />

          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80">
              <span className="h-1.5 w-1.5 rounded-full bg-[#FF5B24]" /> Plataforma de atendimento com IA
            </div>
            <h2 className="mt-6 max-w-md text-4xl xl:text-[42px] leading-[1.15] font-extrabold">
              Todas as conversas do seu negócio, <span className="text-[#FF7A3D]">em um só lugar.</span>
            </h2>
            <p className="mt-4 max-w-md text-white/60 leading-relaxed">
              WhatsApp, Instagram, e-mail e chatbot com agentes de IA que vendem e atendem 24/7.
            </p>
          </div>

          {/* Previa do produto */}
          <div className="relative my-10 max-w-md">
            <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-5 backdrop-blur-sm shadow-2xl">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FF5B24] text-xs font-bold">JK</span>
                <div>
                  <div className="text-sm font-semibold">Juliana Karla</div>
                  <div className="text-xs text-emerald-400">online</div>
                </div>
              </div>
              <div className="mt-4 space-y-2.5 text-sm">
                <div className="w-fit max-w-[85%] rounded-2xl rounded-tl-md bg-white/10 px-4 py-2.5 text-white/90">
                  Olá! Gostaria de mais informações sobre o plano Business.
                </div>
                <div className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-tr-md bg-[#FF5B24] px-4 py-2.5">
                  Olá, Juliana! O Business inclui canais ilimitados e CoreAI Copiloto. Quer agendar uma demo?
                </div>
              </div>
              <div className="mt-3 text-[11px] text-white/40">CoreAI respondeu automaticamente</div>
            </div>
          </div>

          <div className="relative grid grid-cols-2 gap-4 max-w-md">
            <div className="flex gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10"><ShieldCheck size={17} className="text-[#FF7A3D]" /></span>
              <div>
                <div className="text-sm font-semibold">Dados protegidos</div>
                <div className="text-xs text-white/50 mt-0.5">Conexão criptografada e LGPD</div>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10"><Headphones size={17} className="text-[#FF7A3D]" /></span>
              <div>
                <div className="text-sm font-semibold">Suporte humano</div>
                <div className="text-xs text-white/50 mt-0.5">Seg. a Sex., 9h às 18h</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
